import React, { useMemo, useState } from "react";
import { useQuery, gql } from "@apollo/client";

// Month view of who is off: approved leave as solid chips, pending requests
// as dashed ones, and how many professionals are still available each day.

const GET_TEAM_CALENDAR = gql`
  query GetTeamLeaveCalendar($data: TeamCalendarFilter) {
    getTeamLeaveCalendar(data: $data) {
      status
      message
      leaves {
        id
        status
        leaveMode
        startDate
        endDate
        employee {
          id
          fullName
        }
      }
    }
  }
`;

const GET_AVAILABILITY = gql`
  query GetEmployeeAvailabilityForCalendar($data: EmployeeAvailabilityFilter!) {
    getEmployeeAvailability(data: $data) {
      status
      data {
        date
        totalStrength
        availableCount
      }
    }
  }
`;

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MAX_CHIPS = 4;

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const LeaveCalendar = () => {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const first = month;
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const range = { startDate: ymd(first), endDate: ymd(last) };

  const { data, loading } = useQuery(GET_TEAM_CALENDAR, {
    variables: { data: { ...range, includePending: true } },
    fetchPolicy: "network-only",
  });
  const { data: availData } = useQuery(GET_AVAILABILITY, {
    variables: { data: range },
    fetchPolicy: "network-only",
  });

  const leaves = data?.getTeamLeaveCalendar?.leaves || [];
  const availability = useMemo(() => {
    const map = {};
    (availData?.getEmployeeAvailability?.data || []).forEach((d) => {
      map[d.date] = d;
    });
    return map;
  }, [availData]);

  // Leading blanks so the 1st lands under its weekday (Monday first)
  const cells = useMemo(() => {
    const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const blanks = (month.getDay() + 6) % 7;
    const out = Array.from({ length: blanks }, () => null);
    for (let d = 1; d <= monthEnd.getDate(); d += 1) {
      out.push(ymd(new Date(month.getFullYear(), month.getMonth(), d)));
    }
    return out;
  }, [month]);

  const leavesOn = (date) =>
    leaves
      .filter((l) => l.startDate <= date && l.endDate >= date)
      .sort((a, b) => (a.status === b.status ? 0 : a.status === "APPROVED" ? -1 : 1));

  const today = ymd(new Date());
  const shift = (n) =>
    setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => shift(-1)}
            className="px-3 py-1 border rounded bg-white hover:bg-gray-100"
          >
            ‹
          </button>
          <h2 className="text-lg font-semibold text-gray-800 w-44 text-center">
            {month.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
          </h2>
          <button
            onClick={() => shift(1)}
            className="px-3 py-1 border rounded bg-white hover:bg-gray-100"
          >
            ›
          </button>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-green-100 border border-green-400" />
            Approved
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-amber-50 border border-dashed border-amber-400" />
            Pending
          </span>
          <span>½ = half day</span>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading calendar...</p>
      ) : (
        <div className="grid grid-cols-7 gap-px bg-gray-200 border rounded overflow-hidden">
          {WEEKDAYS.map((w) => (
            <div
              key={w}
              className="bg-gray-100 text-xs font-semibold text-gray-600 text-center py-2"
            >
              {w}
            </div>
          ))}
          {cells.map((date, i) => {
            if (!date) return <div key={`blank-${i}`} className="bg-gray-50 min-h-[110px]" />;
            const dayLeaves = leavesOn(date);
            const avail = availability[date];
            return (
              <div
                key={date}
                className={`bg-white min-h-[110px] p-1.5 ${
                  date === today ? "ring-2 ring-inset ring-blue-400" : ""
                }`}
              >
                <div className="flex justify-between items-baseline">
                  <span className="text-xs font-semibold text-gray-700">
                    {Number(date.slice(8))}
                  </span>
                  {avail && (
                    <span
                      className="text-[10px] text-gray-500"
                      title="Professionals available / total (approved leave only)"
                    >
                      {avail.availableCount}/{avail.totalStrength}
                    </span>
                  )}
                </div>
                <div className="mt-1 space-y-0.5">
                  {dayLeaves.slice(0, MAX_CHIPS).map((l) => (
                    <div
                      key={l.id}
                      title={`${l.employee?.fullName} · ${l.status} · ${l.leaveMode}`}
                      className={`text-[11px] truncate px-1 rounded border ${
                        l.status === "APPROVED"
                          ? "bg-green-100 border-green-400 text-green-800"
                          : "bg-amber-50 border-dashed border-amber-400 text-amber-800"
                      }`}
                    >
                      {l.employee?.fullName || "Unknown"}
                      {l.leaveMode?.startsWith("HALF_DAY") ? " ½" : ""}
                    </div>
                  ))}
                  {dayLeaves.length > MAX_CHIPS && (
                    <div className="text-[10px] text-gray-500">
                      +{dayLeaves.length - MAX_CHIPS} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LeaveCalendar;
